import type { JSX } from 'react';
import { Link } from 'react-router';

import { Check, Eye, FlaskConical, NotebookPen, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { caseClientName, caseReporterName, type AgentCase } from '../../model/case';
import CaseStatusBadge from './CaseStatusBadge';

interface CasesTableProps {
  cases: AgentCase[];
  masterProjectId: number;
  subProjectNames: Record<number, string>;
  onPreviewConversation: (agentCase: AgentCase) => void;
  onPromote: (agentCase: AgentCase) => void;
  onAttend: (agentCase: AgentCase) => void;
  onReject: (agentCase: AgentCase) => void;
}

const formatDate = (value: string): string =>
  new Date(value).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });

// El prompt global se edita en el panel de secciones de "nueva versión" del prompt.
const globalPromptPath = (masterProjectId: number): string => `/project/${masterProjectId}/prompt/new-version`;

const projectPromptPath = (agentCase: AgentCase, masterProjectId: number): string =>
  `/project/${masterProjectId}/prompt/${agentCase.subProjectId}`;

const ActionIconButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick?: () => void;
  children: JSX.Element;
}): JSX.Element => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button type="button" variant="ghost" size="icon" aria-label={label} onClick={onClick}>
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="top">{label}</TooltipContent>
  </Tooltip>
);

const CasesTable = ({
  cases,
  masterProjectId,
  subProjectNames,
  onPreviewConversation,
  onPromote,
  onAttend,
  onReject,
}: CasesTableProps): JSX.Element => (
  <div className="overflow-x-auto rounded-xl border border-border-default bg-bg-surface">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-16">#</TableHead>
          <TableHead className="w-24">Estado</TableHead>
          <TableHead>Cliente</TableHead>
          <TableHead className="min-w-[280px]">Detalle</TableHead>
          <TableHead>Reportado por</TableHead>
          <TableHead className="w-32">Fecha</TableHead>
          <TableHead>Resolución</TableHead>
          <TableHead className="w-44 text-right">Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {cases.map((agentCase) => {
          const subProjectName = agentCase.subProjectId != null ? subProjectNames[agentCase.subProjectId] : null;

          return (
            <TableRow key={agentCase.id}>
              <TableCell className="text-text-muted">#{agentCase.id}</TableCell>
              <TableCell>
                <CaseStatusBadge status={agentCase.status} />
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium text-text-default">{caseClientName(agentCase.client)}</span>
                  {subProjectName ? <span className="text-xs text-text-muted">{subProjectName}</span> : null}
                </div>
              </TableCell>
              <TableCell>
                <p
                  className="m-0 line-clamp-2 max-w-md text-sm whitespace-pre-wrap text-text-default"
                  title={agentCase.detail}
                >
                  {agentCase.detail}
                </p>
              </TableCell>
              <TableCell className="text-text-muted">{caseReporterName(agentCase.createdByUser)}</TableCell>
              <TableCell className="text-text-muted">{formatDate(agentCase.createdAt)}</TableCell>
              <TableCell>
                {agentCase.status === 'pending' ? (
                  <span className="text-text-muted">—</span>
                ) : (
                  <div className="flex max-w-xs flex-col text-xs text-text-muted">
                    <span>Por {caseReporterName(agentCase.resolvedByUser)}</span>
                    {agentCase.agentTestScenario ? <span>Escenario: «{agentCase.agentTestScenario.name}»</span> : null}
                    {agentCase.resolutionNote ? (
                      <span className="line-clamp-2" title={agentCase.resolutionNote}>
                        {agentCase.resolutionNote}
                      </span>
                    ) : null}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-0.5">
                  <ActionIconButton label="Ver conversación" onClick={() => onPreviewConversation(agentCase)}>
                    <Eye aria-hidden />
                  </ActionIconButton>
                  {agentCase.status === 'pending' ? (
                    <>
                      <ActionIconButton label="Crear escenario" onClick={() => onPromote(agentCase)}>
                        <FlaskConical aria-hidden />
                      </ActionIconButton>
                      <DropdownMenu>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <DropdownMenuTrigger asChild>
                                <Button type="button" variant="ghost" size="icon" aria-label="Mejorar prompt">
                                  <NotebookPen aria-hidden />
                                </Button>
                              </DropdownMenuTrigger>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">Mejorar prompt</TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={globalPromptPath(masterProjectId)}>Editar prompt global</Link>
                          </DropdownMenuItem>
                          {agentCase.subProjectId != null ? (
                            <DropdownMenuItem asChild>
                              <Link to={projectPromptPath(agentCase, masterProjectId)}>Editar prompt del proyecto</Link>
                            </DropdownMenuItem>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <ActionIconButton label="Marcar atendido" onClick={() => onAttend(agentCase)}>
                        <Check aria-hidden />
                      </ActionIconButton>
                      <ActionIconButton label="Rechazar" onClick={() => onReject(agentCase)}>
                        <X aria-hidden />
                      </ActionIconButton>
                    </>
                  ) : null}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

export default CasesTable;
